import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CommitProgress from "../components/CommitProgress";
import CommonDialog from "../components/CommonDialog";
import ComparisonContainer from "../components/ComparisonContainer";
import Paper from "@mui/material/Paper";
import React, { useEffect } from "react";
import SalesOrderLinesContainer from "./SalesOrderLinesContainer";
import SalesOrderToolbar from "../toolbars/SalesOrderToolbar";
import Stack from "@mui/material/Stack";
import StatusDialog from "../components/StatusDialog";
import TabbedDialogContent from "../components/TabbedDialogContent";
import Typography from "@mui/material/Typography";
import { IReadVerbPayload } from "../declarations/IReadVerbPayload";
import { useGetCustomerComparison } from "../hooks/Customer/useGetCustomerComparison";
import { useGetSalesAccIDCS } from "../hooks/SalesOrder/CS/useGetSalesAccIDCS";
import { useGetSalesOrderHeader } from "../hooks/SalesOrder/useGetSalesOrderHeader";
import { useGetSalesOrderHeaderChargeComparison } from "../hooks/SalesOrder/useGetSalesOrderHeaderChargeComparison";
import { useGetSalesOrderHeaderComparison } from "../hooks/SalesOrder/useGetSalesOrderHeaderComparison";
import { useGetSalesOrderHeaderStatusD365 } from "../hooks/SalesOrder/D365/useGetSalesOrderHeaderStatusD365";
import { useGetSalesOrderInit } from "../hooks/SalesOrder/CS/useGetSalesOrderInit";
import { useGetVatNumComparison } from "../hooks/Tax/useGetVatNumComparison";
import { useLayoutContext } from "@ntg/component-library/contexts";
import { useParams } from "react-router-dom";

export default function SalesOrderContainer() {
    const { id } = useParams<{ id: string }>();
    const { SetPrimaryTitle, SetSecondaryHeader } = useLayoutContext();

    const { data: salesOrderHeader, isLoading: isLoadingHeader } =
        useGetSalesOrderHeader(Number(id));

    const { data: salesOrderInit, isLoading } = useGetSalesOrderInit(
        Number(id),
    );

    const SalesOrderHeaderPayload: IReadVerbPayload = {
        keys: {
            salesOrderNumber: salesOrderInit?.salesOrderNumber ?? "",
            dataAreaId: salesOrderInit?.dataAreaID ?? "",
        },
    };

    const { data: salesOrderStatus, isLoading: isLoadingStatus } =
        useGetSalesOrderHeaderStatusD365(SalesOrderHeaderPayload);

    const { data: salesAccIDCS, isLoading: salesAccIDCSLoading } =
        useGetSalesAccIDCS(Number(id));

    useEffect(() => {
        SetPrimaryTitle(
            isLoading
                ? `Loading...`
                : `Sales Order ${salesOrderInit?.salesOrderNumber}`,
        );

        SetSecondaryHeader(
            <SalesOrderToolbar
                D365Status={salesOrderStatus || "Loading"}
                OrderID={Number(id)}
            />,
        );

        return () => {
            // Copilot Comment: Cleanup function to reset layout headers when component unmounts
            SetPrimaryTitle("");
            SetSecondaryHeader(null);
        };
    }, [SetPrimaryTitle, isLoading, salesOrderInit, salesOrderStatus]);

    return (
        <Stack spacing={2}>
            <Card component={Paper} sx={{ padding: 1 }}>
                <CardContent
                    sx={{
                        display: "flex",
                        alignContent: "center",
                        gap: 2,
                        flexDirection: "column",
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={6}
                    >
                        <Typography variant="h5">
                            Contact: {salesOrderHeader?.contact}
                        </Typography>
                        <Typography variant="h5">
                            Boat: {salesOrderHeader?.boat}
                        </Typography>
                        <Typography variant="h5">
                            D365 Company: {salesOrderHeader?.dataAreaId}
                        </Typography>
                    </Stack>

                    <SalesOrderLinesContainer
                        dataAreaID={salesOrderInit?.dataAreaID}
                        orderNumber={salesOrderInit?.salesOrderNumber}
                    />
                </CardContent>
            </Card>

            <CommonDialog
                title="Sales Order Comparison"
                expectedPaths={["customer", "header", "vatnum", "headercharge"]}
            >
                <TabbedDialogContent
                    customerContent={
                        <ComparisonContainer
                            payload={{
                                keys: {
                                    customerAccount: salesOrderInit?.custAccount ?? "",
                                    dataAreaId: salesOrderInit?.dataAreaID ?? "",
                                    salesAccID: salesAccIDCSLoading ? 0 : salesAccIDCS ?? 0,
                                },
                            } satisfies IReadVerbPayload}
                            useComparison={useGetCustomerComparison}
                        />
                    }
                    headerContent={
                        <ComparisonContainer
                            payload={{
                                keys: {
                                    salesOrderNumber: salesOrderInit?.salesOrderNumber ?? "",
                                    dataAreaId: salesOrderInit?.dataAreaID ?? "",
                                    orderID: Number(id) || 0,
                                },
                            } satisfies IReadVerbPayload}
                            useComparison={useGetSalesOrderHeaderComparison}
                        />
                    }
                    vatNumContent={
                        <ComparisonContainer
                            payload={{
                                keys: {
                                    countryRegionId: salesOrderInit?.taxCertCountryRegionId ?? "",
                                    vatNum: "",
                                    dataAreaId: salesOrderInit?.dataAreaID ?? "",
                                    salesAccTaxCertID: 0,
                                },
                            } satisfies IReadVerbPayload}
                            useComparison={useGetVatNumComparison}
                        />
                    }
                    headerChargeContent={
                        <ComparisonContainer
                            payload={{
                                keys: {
                                    salesOrderNumber: salesOrderInit?.salesOrderNumber ?? "",
                                    dataAreaId: salesOrderInit?.dataAreaID ?? "",
                                    orderID: Number(id) || 0,
                                    ChargeLineNumber: 1,
                                },
                            } satisfies IReadVerbPayload}
                            useComparison={useGetSalesOrderHeaderChargeComparison}
                        />
                    }
                />
            </CommonDialog>

            <StatusDialog title="Commit Progress" expectedPaths={["commit"]}>
                <CommitProgress />
            </StatusDialog>
        </Stack>
    );
}
